import { mdiCheck, mdiClose, mdiPencil, mdiDelete, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  itemMovementDefaultValue,
  StockItemMovementModel,
  UniqueProductModel,
} from "../../../models/stock_transfer_model";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { ItemModelV2 } from "../../../models/item_model";
import { fetchGet } from "../../../../../service/api_client";
import { WarehouseModel } from "../../../models/warehouse_model";
import ModalDialog from "../../../../../ui/new_components/common/modal";
import { generate_id } from "../../../journals/destination_stock_voucher/components/item_row_status";
import NewItemSearchByIdField from "../../../items/item_search_by_ids";
import Papa from "papaparse";
import { ScannedBarCodeScanForDeviceFromPTAPI } from "../service/stock_transfer_repo";

interface ItemRowProps {
  component?: StockItemMovementModel;
  onSubmit: (data: StockItemMovementModel) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: StockItemMovementModel) => void;
  existingItems: StockItemMovementModel[];
  from_warehouse: WarehouseModel;
  availableItems?: {
    item_id: string;
    warehouse_id: string;
    available_units: number;
  }[];
}

const ItemRowStockTransfer: FunctionComponent<ItemRowProps> = (props) => {
  const [data, setData] = useState<StockItemMovementModel>({
    ...itemMovementDefaultValue,
  });
  const { showToast } = useToast();
  const { edit, onSubmit, existingItems, availableItems } = props;
  const [editable, setEditable] = useState(false);
  const [itemEdit, setItemEdit] = useState(false);
  const [addProduct, setAddProduct] = useState(false);
  const [scan, setScan] = useState<boolean>(false);
  const [check, setMdiCheck] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalUnits, setTotalUnits] = useState(0);
  const [prevUnits, setPrevUnits] = useState(0);
  const location = useLocation();
  const isCreatePath = location.pathname.endsWith("/create");
  const uniqueIds = useRef<any[]>([]);
  const alreadyAddedIds = useRef<string[]>([]); // store IDs added during creation
  const [scannedDevices, setScannedDevices] = useState<UniqueProductModel[]>(
    [],
  );
  const itemIdsRef = useRef<any[]>([]);
  const itemIds = availableItems?.map((item: any) => item.item_id) || [];
  const [scannedID, setScannedID] = useState<string>("");
  const [choiceModal, setChoiceModal] = useState<boolean>(false);
  const [scanTimeout, setScanTimeout] = useState<NodeJS.Timeout | null>(null);
  const [scanBuffer, setScanBuffer] = useState<string>("");

  useEffect(() => {
    itemIdsRef.current = (availableItems ?? []).map(
      (item: any) => item.item_id,
    );
    if ((props.component?.uniqueIds?.length ?? 0) > 0) {
      alreadyAddedIds.current = (props.component?.uniqueIds ?? [])
        .map((u: any) => u.unique_id?.trim())
        .filter(Boolean);
    } else {
      alreadyAddedIds.current = [];
    }
    setEditable(edit);
    if (props.component) {
      setData(props.component);
    } else {
      setData({ ...itemMovementDefaultValue });
    }
  }, [edit, props.component, availableItems]);

  useEffect(() => {
    if (props.component) {
      setData((prevData) => ({
        ...prevData,
        ...props.component,
      }));
    }
  }, [props.component]);

  const scannedInputFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scan && scannedInputFieldRef.current) {
      scannedInputFieldRef.current.focus();
    }
  }, [scan]);

  const [initialRows, setInitialRows] = useState<
    { id: string; unique_id: string; item_name: string }[]
  >([]);

  useEffect(() => {
    if (data.movement_type) {
      setItemEdit(true);
      // Find the existing item in the list
      const existingItem = existingItems.find((item) => item.id === data.id);

      // Get the existing unit_no, defaulting to 0 if not found
      const existingUnitNo = existingItem
        ? Number(existingItem.unit_no) || 0
        : 0;

      // Update state with the existing unit_no
      setPrevUnits(existingUnitNo);
    }
  }, [existingItems, data.id]); // Track changes in existingItems and data.id

  useEffect(() => {
    if (!data.unit_no) return; // Ensure unit_no is valid
    if (
      data.item?.subcategory !== "Finished Goods" &&
      data.item?.subcategory !== "IT and Electronic Hardware"
    ) {
      return;
    }
    if (!data.unit_no) return; // Ensure unit_no is valid

    const rows = Array.from({ length: data.unit_no }, (_, index) => {
      const existingUniqueId = data.uniqueIds?.[index]?.unique_id || ""; // Get existing unique_id if present

      return {
        id: `${"UN"}-${generate_id(8, "numeric")}`, // Unique ID generation
        unique_id: existingUniqueId,
        item_name: data.item?.name || "",
      };
    });

    setInitialRows(rows);
    setData((prevData) => ({ ...prevData, uniqueIds: rows }));
  }, [data.unit_no]); // Depend on data.unit_no to update rows dynamically

  useEffect(() => {
    if (data.item?.item_code && props.from_warehouse?.name) {
      checkUniqueIdsExists();
      if (scannedID) {
        getDeviceIDs(scannedID);
      }
    }
  }, [data.item?.item_code, props.from_warehouse?.name, scannedID]);

  const checkUniqueIdsExists = async () => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/unique-ids?warehouse_id=${props.from_warehouse?.id}&item_code=${data.item?.item_code}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      if (res.success) {
        // Later in checkUniqueIdsExists:
        // uniqueIds.current = res.result?.map((item: any) => item.unique_id);
        const fetchedIds =
          res.result
            ?.map((item: any) => item.unique_id?.trim())
            .filter(Boolean) ?? [];

        const combined = [...fetchedIds, ...(alreadyAddedIds.current ?? [])];

        // Remove duplicates using Set
        const uniqueCombined = Array.from(new Set(combined));

        // Update refs
        uniqueIds.current = uniqueCombined;
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching uniqueIds",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIDs = async (scanned_id: string) => {
    try {
      if (scanned_id && scanned_id.length !== 6 && scanned_id.length !== 8) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID",
        });
        return false;
      }
      setLoading(true);
      const res = await ScannedBarCodeScanForDeviceFromPTAPI(
        data.item?.item_code,
        props.from_warehouse.id,
        scanned_id,
      );

      if (res.success) {
        setScannedDevices((prev) => {
          const existing = new Set(prev.map((d) => d.unique_id));
          const newOnes = res.devices.filter(
            (d: any) => !existing.has(d.unique_id),
          );
          if (newOnes.length === 0) {
            showToast({
              type: ToastType.warning,
              text: "No devices found/All scanned devices are already added.",
            });
            return prev;
          }
          showToast({
            type: ToastType.success,
            text: `Added ${newOnes.length} new device(s).`,
          });
          return [...prev, ...newOnes];
        });
        setScannedID("");
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
      setLoading(false);
      // showToast({ type: ToastType.error, text: res.error });
      return;
    } catch (error: any) {
      setLoading(false);
      showToast({
        type: ToastType.error,
        text: error.message,
      });
    }
  };

  const handleAddingDeviceIds = () => {
    setData((prev) => ({
      ...prev,
      uniqueIds: scannedDevices,
      unit_no: scannedDevices.length,
    }));
    if (!Array.isArray(scannedDevices) || scannedDevices.length === 0) {
      showToast({ type: ToastType.error, text: "No devices to add." });
      return;
    }

    for (const dev of scannedDevices) {
      if (!dev.unique_id || dev.unique_id.trim() === "") {
        showToast({
          type: ToastType.error,
          text: "Invalid unique ID detected.",
        });
        return;
      }
      if (!uniqueIds.current.includes(dev.unique_id)) {
        showToast({
          type: ToastType.error,
          text: `Unique ID does not exist for ${dev.unique_id}`,
        });
        return;
      }
    }

    // data.uniqueIds = scannedDevices;
    setAddProduct(false);
    setScan(false);
    setChoiceModal(false);
  };

  const handleAddingUniqueIds = async () => {
    if (!Array.isArray(data.uniqueIds)) return;
    for (const object of data.uniqueIds) {
      if (!object.unique_id || object.unique_id.trim() === "") {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Unique ID, dont keep empty field!!",
        });
        return;
      } else if (!uniqueIds.current.includes(object.unique_id)) {
        showToast({
          type: ToastType.error,
          text: `Unique ID does not exist for ${object.unique_id}`,
        });
        return;
      }
    }

    // If loop completes without triggering any return
    setAddProduct(false);
    setScan(false);
    setChoiceModal(false);
  };

  const downloadCsv = () => {
    const ids = uniqueIds.current || [];
    if (!ids.length) {
      showToast({ type: ToastType.info, text: "No IDs to download." });
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const header = ["Device ID"];
      const rows = ids.map((id) => [id]);
      const csvRows = [header, ...rows];
      const csvString = csvRows.map((r) => r.join(",")).join("\n");
      const csvContent = "\uFEFF" + csvString;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "device_ids.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setLoading(false);
    }, 100); // Slight delay to allow UI (like spinner) to render
  };

  // Use data.uniqueIds as the source of truth
  const handleInputChange = async (index: number, value: string) => {
    const updatedUniqueIds: UniqueProductModel[] = Array.isArray(data.uniqueIds)
      ? [...data.uniqueIds]
      : [];

    updatedUniqueIds[index].unique_id = value;
    setData({ ...data, uniqueIds: updatedUniqueIds });
  };

  // Add new row
  const handleAdd = () => {
    const newRow: UniqueProductModel = {
      unique_id: "",
      item_name: data.item?.name || "",
    };

    setData({
      ...data,
      uniqueIds: [...(data.uniqueIds || []), newRow],
    });
  };

  const [pendingUnits, setPendingUnits] = useState<number>(data.unit_no || 0);
  const handleDeleteDevice = (device_id: string) => {
    setScannedDevices((prev) => prev.filter((d) => d.unique_id !== device_id));
  };

  // Delete row
  const handleDelete = (index: number) => {
    const updatedUniqueIds = [...(data.uniqueIds || [])];
    updatedUniqueIds.splice(index, 1);
    setData({ ...data, uniqueIds: updatedUniqueIds });
  };

  const updatePrice = async (requestedUnits: number) => {
    if (requestedUnits > totalUnits) {
      showToast({
        type: ToastType.warning,
        text: "Requested units exceed available stock.",
      });
      return;
    } else {
      setData((prevData) => ({
        ...prevData,
        unit_no: requestedUnits,
      }));
    }
  };

  useEffect(() => {
    const available = availableItems?.find(
      (item: any) =>
        item.item_id === (data.item?.id ?? data.item_id) &&
        item.warehouse_id === props.from_warehouse?.id,
    );

    const availableUnits = available?.available_units ?? 0;
    setTotalUnits(availableUnits + prevUnits);
  }, [availableItems, data.item, props.from_warehouse, totalUnits]);

  const handleItemSelect = async (selectedItem: ItemModelV2) => {
    if (props.from_warehouse?.name === undefined) {
      showToast({
        type: ToastType.warning,
        text: "Please select from warehouse before entering item.",
      });
      return;
    }
    if (data.unit_no) {
      setData((prevData) => ({
        ...prevData,
        unit_no: 0, // Reset units when a new item is selected
      }));
    }

    // Find available units from availableItems using selected item ID and warehouse ID
    const available = availableItems?.find(
      (item: any) =>
        item.item_id === selectedItem.id &&
        item.warehouse_id === props.from_warehouse?.id,
    );

    const availableUnits = available?.available_units ?? 0;
    setTotalUnits(availableUnits + prevUnits); // <-- Set available units here

    setData((prevData) => ({
      ...prevData,
      // id: generate_id(),//dont generate its generating in backend
      item: selectedItem,
      item_id: selectedItem.id,
      item_code: selectedItem.item_code,
      unit_measure: selectedItem.unit?.formal_name,
    }));
  };

  const onDone = () => {
    console.log("data.uniqueIds:", data.uniqueIds);
    if (data.uniqueIds) {
      const hasEmpty = data.uniqueIds.some(
        (object) => !object.unique_id?.trim(),
      );
      if (hasEmpty) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Unique ID for all rows",
        });
        return;
      }
    }
    console.log("data:", data.id);
    const isDuplicate = (existingItems ?? []).some(
      (entry) =>
        entry.item?.item_code === data.item?.item_code && entry.id !== data.id, // ignore current row being edited
    );

    if (isDuplicate) {
      showToast({
        type: ToastType.warning,
        text: "Item already added for same item code!!",
      });
      return;
    }

    if (
      data.item?.subcategory !== "Finished Goods" &&
      data.item?.subcategory !== "IT and Electronic Hardware"
    ) {
      delete data.uniqueIds;
    } else if (data.uniqueIds) {
      console.log("ENTERED");
      data.uniqueIds.forEach((object) => {
        if (object.unique_id === "" || object.unique_id === undefined) {
          showToast({
            type: ToastType.error,
            text: "Please enter a valid Item ID",
          });
          return;
        }
      });
    }
    if (
      data.item.type === "Product" &&
      (data?.uniqueIds?.length ?? 0) > 0 &&
      data.unit_no !== data.uniqueIds?.length
    ) {
      showToast({
        type: ToastType.error,
        text: "Please match the same count of devices with entered units!!!",
      });
      return;
    }

    if (!data.item || !data.unit_no || !props.from_warehouse?.name) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, Warehouse, and units!!",
      });
      setEditable(true);
      return;
    }
    console.log("data getting ", data);
    onSubmit(data);
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = () => {
    const headers = ["Item Code", "Unique ID", "Item Name"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "stock_transfer_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    console.log("File selected", file);
    if (!file) return;

    const validMimeTypes = ["text/csv", "application/vnd.ms-excel"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!validMimeTypes.includes(file.type) && fileExtension !== "csv") {
      showToast({ type: ToastType.error, text: "Only CSV files are allowed." });
      event.target.value = "";
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const rawData = results.data as Record<string, string>[];

        if (rawData.length === 0) {
          showToast({
            type: ToastType.error,
            text: "CSV file is empty or invalid.",
          });
          return;
        }

        const parsedData = rawData.map((row) => {
          const trimmedRow: Record<string, string> = {};
          for (const key in row) {
            const trimmedKey = key.trim();
            const trimmedValue = row[key]?.trim() ?? "";
            trimmedRow[trimmedKey] = trimmedValue;
          }
          return trimmedRow;
        });

        const requiredHeaders = ["Item Code", "Unique ID", "Item Name"];
        const firstRow = parsedData[0];
        const missingHeaders = requiredHeaders.filter(
          (key) => !(key in firstRow),
        );

        if (missingHeaders.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Missing column(s): ${missingHeaders.join(", ")}`,
          });
          return;
        }

        const formattedData: UniqueProductModel[] = parsedData.map((row) => ({
          unique_id: row["Unique ID"],
          item_name: row["Item Name"],
          item_code: row["Item Code"],
        }));

        // Check for duplicate Unique IDs within the uploaded CSV
        const seen: Set<string> = new Set();
        const duplicateInCSV: string[] = [];

        for (const item of formattedData) {
          if (!item.unique_id) continue;
          if (seen.has(item.unique_id)) {
            duplicateInCSV.push(item.unique_id);
          } else {
            seen.add(item.unique_id);
          }
        }

        if (duplicateInCSV.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Duplicate Unique ID(s) in CSV: ${[
              ...new Set(duplicateInCSV),
            ].join(", ")}`,
          });
          return;
        }

        // Check against already existing IDs
        const duplicateExistingIds = formattedData
          .map((item) => item.unique_id!)
          .filter((id) => itemIds.includes(id));

        if (duplicateExistingIds.length > 0) {
          showToast({
            type: ToastType.error,
            text: `The following Unique ID(s) already exist: ${[
              ...new Set(duplicateExistingIds),
            ].join(", ")}`,
          });
          return;
        }

        // Check that all item_codes match the current data.item_code
        const mismatchedItems = formattedData.filter(
          (item) => item.item_code !== data.item_code,
        );

        if (mismatchedItems.length > 0) {
          const mismatchedCodes = [
            ...new Set(mismatchedItems.map((item) => item.item_code)),
          ].join(", ");
          showToast({
            type: ToastType.error,
            text: `Device ID(s) ${mismatchedCodes} cannot be Inwarded on Device ID ${
              data.item_code ?? "--"
            }`,
          });
          return;
        }

        const newUniqueIds = [...(data.uniqueIds ?? []), ...formattedData];

        const finalUniqueIds = Array.from(
          new Map(newUniqueIds.map((item) => [item.unique_id, item])).values(),
        );

        console.log("Final Unique IDs", finalUniqueIds);

        setData((prev) => ({
          ...prev,
          uniqueIds: finalUniqueIds,
        }));

        showToast({
          type: ToastType.success,
          text: `${formattedData.length} new Unique ID(s) uploaded.`,
        });
      },
    });
  };

  const handleScanBarcodeChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newChar = e.target.value;

    if (scanTimeout) {
      clearTimeout(scanTimeout);
    }

    if (newChar.length > 1) {
      const manualInput = newChar.trim();

      if (manualInput.length === 6 || manualInput.length === 8) {
        setScannedID(manualInput);
        setScanBuffer("");
        await getDeviceIDs(manualInput);
        return;
      } else if (manualInput.length < 6) {
        // Don't show error for manual input less than 6, just wait
        setScanBuffer(manualInput);
      } else {
        // More than 8 characters
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
        e.target.value = "";
        return;
      }
    } else {
      //accumulate each character as buffer
      const updatedBuffer = scanBuffer + newChar;
      setScanBuffer(updatedBuffer);

      e.target.value = "";

      if (updatedBuffer.length > 8) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
        return;
      }
      if (updatedBuffer.length === 8) {
        setScannedID(updatedBuffer);
        setScanBuffer("");
        await getDeviceIDs(updatedBuffer);
        return;
      }
    }

    const newTimeout = setTimeout(async () => {
      const currentBuffer = scanBuffer + (newChar.length === 1 ? newChar : "");
      console.log("Timeout triggered, current buffer:", currentBuffer);

      if (currentBuffer.length === 6) {
        // Accept 6-digit code after timeout (scanner finished)
        console.log("6-digit scan accepted after timeout:", currentBuffer);
        setScannedID(currentBuffer);
        setScanBuffer("");
        await getDeviceIDs(currentBuffer);
      } else if (currentBuffer.length > 0 && currentBuffer.length < 6) {
        // Only show error if less than 6 digits
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
      } else if (currentBuffer.length > 6 && currentBuffer.length < 8) {
        // Between 6-8 but not exactly 6 or 8
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID (6 or 8 characters)",
        });
        setScanBuffer("");
      }
    }, 300); // Shorter timeout to be more responsive

    setScanTimeout(newTimeout);
  };

  const buttonClass =
    "border rounded-lg px-4 py-2 hover:bg-gray-800 hover:text-white";

  return (
    <div>
      <div className="p-1">
        {choiceModal && (
          <ModalDialog
            show={choiceModal}
            title={
              scan
                ? "Scan Barcode"
                : addProduct
                  ? "Upload CSV"
                  : "Choose Upload Method"
            }
            onClose={() => {
              setChoiceModal(false);
              setScan(false);
              setAddProduct(false);
              setScannedID("");
              setScanBuffer("");
              if (scanTimeout) {
                clearTimeout(scanTimeout);
              }
            }}
          >
            {/* Step 1: Show scan section if selected */}
            {scan ? (
              <section>
                <div className="bg-gray-50 p-3 m-2 border rounded-lg">
                  <input
                    type="text"
                    value={scannedID}
                    placeholder="Scan Barcode"
                    className="font-semibold h-full"
                    ref={scannedInputFieldRef}
                    disabled={loading}
                    onChange={handleScanBarcodeChange}
                  />
                  {/* <Scanner
                    onScan={(detected: IDetectedBarcode[]) => {
                      if (detected.length > 0) {
                        setScannedCode(detected[0].rawValue);
                      }
                    }}
                    onError={(err) => {
                      console.error("QR scan error:", err);
                    }}
                    formats={["qr_code"]}
                    scanDelay={500}
                  /> */}
                </div>
                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm text-left table-auto border-collapse">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-2">Device ID</th>
                        <th className="px-4 py-2">Item Name</th>
                        <th className="px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scannedDevices.map((device) => (
                        <tr
                          key={device.unique_id}
                          className={`border-b hover:bg-gray-50 ${
                            device.inventory_found === false
                              ? "bg-gray-200"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-2">{device.unique_id}</td>
                          <td className="px-4 py-2">{device.item_name}</td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() =>
                                handleDeleteDevice(device.unique_id)
                              }
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {scannedDevices.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center py-3 text-gray-500"
                          >
                            No scanned devices.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2 my-4">
                  <button
                    className={buttonClass}
                    onClick={() => {
                      setChoiceModal(true);
                      setScan(false);
                      setScannedID("");
                      setScanBuffer("");
                      if (scanTimeout) {
                        clearTimeout(scanTimeout);
                      }
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className={buttonClass}
                    onClick={() => {
                      handleAddingDeviceIds();
                    }}
                  >
                    {props.existingItems ? "Update" : "Create"}
                  </button>
                </div>
              </section>
            ) : addProduct ? (
              // Step 2: Show CSV upload section if selected
              <div className="bg-white p-6 rounded-md shadow-lg w-96">
                {/* <p>Available Devices: {(uniqueIds.current || []).join(", ")}</p> */}
                <div className="m-1 border rounded p-1">
                  <p>Get Available Devices here in csv..</p>

                  <label
                    onClick={downloadCsv}
                    className="bg-myPrimaryColor text-white rounded-lg px-3 py-1 text-sm cursor-pointer duration-300 m-1"
                  >
                    Available Devices
                  </label>
                </div>
                <div className="flex justify-between">
                  <h2 className="text-md font-semibold mb-4">
                    Add Product List
                  </h2>

                  <div className="">
                    {/* <label
                      onClick={handleDownloadTemplate}
                      className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                    >
                      Template
                    </label>
                    <label className="bg-myPrimaryColor text-white p-1 rounded-md cursor-pointer">
                      Upload
                      <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        onChange={handleCSVUpload}
                        className="hidden"
                      />
                    </label> */}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <div className="overflow-y-auto max-h-60 border border-gray-300">
                    <table className="w-full border border-gray-300">
                      {/* Table Head */}
                      <thead className="bg-gray-200">
                        <tr>
                          <th className="p-2 border">Unique Id</th>
                          <th className="p-2 border">Item Name</th>
                          <th className="p-2 border">Actions</th>
                        </tr>
                      </thead>

                      {/* Table Body */}
                      <tbody>
                        {/* {rows.map((row: any, index: any) => ( */}
                        {(data.uniqueIds || []).map((row: any, index) => (
                          <tr key={row.id} className="text-center">
                            <td className="p-2 border">
                              <input
                                type="text"
                                value={row.unique_id || ""}
                                onChange={(e) =>
                                  handleInputChange(index, e.target.value)
                                }
                                className="p-1 border rounded w-full focus:outline-none"
                                placeholder="Enter Unique Id"
                              />
                            </td>

                            <td className="p-2 border">{row.item_name}</td>
                            <td className="p-2 border">
                              <div
                                onClick={() => handleDelete(index)}
                                className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer inline-block"
                              >
                                <Icon path={mdiDelete} className="h-4 w-4" />
                              </div>
                            </td>
                          </tr>
                        ))}

                        {/* Add New Row Button */}
                        {/* <tr>
                          <td colSpan={3} className="p-2 border text-center">
                            <div
                              onClick={handleAdd}
                              className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200 cursor-pointer inline-flex items-center space-x-1"
                            >
                              <Icon path={mdiPlus} className="h-4 w-4" />
                              <span>Add Row</span>
                            </div>
                          </td>
                        </tr> */}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end items-center mt-4">
                    <button
                      className="bg-myPrimaryColor text-white p-1 rounded-md mt-4 "
                      onClick={() => {
                        handleAddingUniqueIds();
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-2 my-4">
                  <button
                    className={buttonClass}
                    onClick={() => {
                      setChoiceModal(true);
                      setAddProduct(false);
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className={buttonClass}
                    onClick={() => {
                      handleAddingUniqueIds();
                    }}
                  >
                    {props.existingItems ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            ) : (
              // Step 3: Show initial method choice
              <div className="flex justify-center gap-6 py-4">
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() => {
                    setScan(true);
                    setAddProduct(false);
                  }}
                >
                  Scan ID
                </button>
                <button
                  type="button"
                  title="Required fields - Item Code, Unique ID, Item Name"
                  className={buttonClass}
                  onClick={() => {
                    setAddProduct(true);
                    setScan(false);
                  }}
                >
                  Upload CSV
                </button>
              </div>
            )}
          </ModalDialog>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onDone();
        }}
        className="bg-white p-1"
      >
        <div className="grid grid-cols-7 gap-1 items-center">
          <div>
            <input
              className="text-left p-1 focus:outline-none border rounded w-full"
              placeholder="item-code"
              disabled
              value={data.item_code}
              readOnly
            />
            <span className="text-xs text-gray-500">&nbsp;</span>
          </div>

          {data.item_name && (
            <div className="col-span-2">
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="item-code"
                disabled
                value={data.item_name}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
          )}
          {!data.item_name && (
            <div className="col-span-2">
              <NewItemSearchByIdField
                disabled={!editable || props.disabled || itemEdit}
                value={data.item}
                item_ids={itemIds}
                onSelect={handleItemSelect}
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
          )}
          <div>
            <input
              className="text-left p-1 focus:outline-none border rounded w-full"
              placeholder="unit"
              disabled={!editable || props.disabled}
              value={data.unit_measure}
              onChange={(e) => {
                setData((prev) => ({
                  ...prev,
                  unit_measure: e.target.value,
                }));
              }}
            />
            <span className="text-xs text-gray-500">&nbsp;</span>
          </div>
          <div>
            <input
              value={data.unit_no || ""}
              disabled={!editable || props.disabled}
              onChange={(e) => {
                const unitNo = Number(e.target.value) || 0;
                // setData((prevData) => ({
                //   ...prevData,
                //   unit_no: unitNo,
                // }));
                if (props.from_warehouse) {
                  updatePrice(unitNo);
                }
              }}
              // disabled={!editable || props.disabled}
              type="text"
              className="text-left p-1 focus:outline-none border rounded w-full"
            />
            <span className="text-xs text-gray-500">
              Avail Units:{totalUnits}
            </span>
          </div>
          <div>
            {data.item?.type === "Product" &&
            (data.item?.subcategory === "Finished Goods" ||
              data.item?.subcategory === "IT and Electronic Hardware") ? (
              <div
                onClick={() => {
                  setChoiceModal(true);
                }}
                className="text-left p-1 focus:outline-none border rounded text-blue-800 w-full cursor-pointer"
              >
                <div className="flex items-center space-x-2">
                  <Icon path={mdiPlus} className="h-6 w-4" />
                  {/* <span>
                    {(data.uniqueIds?.length ?? 0) > 0
                      ? `${data.uniqueIds?.length ?? 0} items added`
                      : "Add/Upload"}
                  </span> */}
                  <span>
                    {(data.uniqueIds?.filter((u) => u.unique_id)?.length ?? 0) >
                    0
                      ? `${
                          data.uniqueIds?.filter((u) => u.unique_id)?.length ??
                          0
                        } items added`
                      : "Add/Upload"}
                  </span>
                </div>
              </div>
            ) : (
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="unique"
                disabled={true}
                // value={}
                onClick={() => {}}
              />
            )}
            <span className="text-xs text-gray-500">&nbsp;</span>
          </div>

          <div className="flex gap-2 items-center justify-center">
            {props.component ? (
              !props.disabled && (
                <div>
                  <div className="flex justify-center gap-2">
                    {editable ? (
                      <>
                        <div
                          onClick={() => {
                            if (!window.confirm("Are you sure!")) return;
                            onDone();
                            setEditable(false);
                          }}
                          className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiCheck} className="h-4 w-4" />
                        </div>
                        <div
                          onClick={() => {
                            setData(
                              props.component || itemMovementDefaultValue,
                            );
                            setEditable(false);
                          }}
                          className="bg-gray-100 p-1 rounded text-gray-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiClose} className="h-4 w-4" />
                        </div>
                      </>
                    ) : (
                      (data.voucher_id || isCreatePath) && (
                        <div
                          onClick={() => {
                            setEditable(true);
                            setMdiCheck(false);
                          }}
                          className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiPencil} className="h-4 w-4" />
                        </div>
                      )
                    )}
                    {/* {data.voucher_id && ( */}
                    <div
                      onClick={() =>
                        props.onDelete && props.onDelete(props.component!)
                      }
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiDelete} className="h-4 w-4" />
                    </div>
                    {/* )} */}
                  </div>
                  <span className="text-xs text-gray-500">&nbsp;</span>
                </div>
              )
            ) : (
              <div>
                <div
                  onClick={() => {
                    setEditable(false);
                    onDone();
                  }}
                  className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                >
                  {/* <Icon path={mdiPlus} className="h-4 w-4" /> */}
                  <span> + Add</span>
                </div>
                <span className="text-xs text-gray-500">&nbsp;</span>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ItemRowStockTransfer;
